import styled, { css } from 'styled-components';
import { palette } from 'styled-tools';

import lockIcon from 'icons/lock-filled.svg';
import playIcon from 'icons/play-filled.svg';
import videoIcon from 'icons/video-chip.svg';

import { ContentChipSize } from './types';

const chipBackground = css`
	background: rgba(72, 72, 72, 0.4);
	backdrop-filter: blur(10px);
`;

// iOS draws its thin-material badges over a list row's gradient as a light translucent fill, where
// over card artwork the same material reads dark. The now-playing badge shares this token.
export const ROW_BADGE_BACKGROUND = 'rgba(255, 255, 255, 0.33)';

const DIMENSIONS = {
	default: { lock: 28, lockGlyph: 16, play: 22, glyph: 10, fontSize: 10 },
	compact: { lock: 20, lockGlyph: 11, play: 16, glyph: 7, fontSize: 9 },
} as const;

const chipPosition = css<{ $margin?: number }>`
	${p =>
		p.$margin !== undefined &&
		css`
			bottom: ${p.$margin}px;
			left: ${p.$margin}px;
			position: absolute;
		`}
`;

export const LockBadge = styled.div<{ $margin?: number; $size: ContentChipSize; $inline?: boolean }>`
	${p =>
		p.$inline
			? css`
					background: ${ROW_BADGE_BACKGROUND};
					backdrop-filter: blur(10px);
				`
			: chipBackground}
	${chipPosition}
	align-items: center;
	border-radius: 50%;
	box-sizing: border-box;
	display: flex;
	flex-shrink: 0;
	height: ${p => DIMENSIONS[p.$size].lock}px;
	justify-content: center;
	width: ${p => DIMENSIONS[p.$size].lock}px;
`;

export const LockGlyph = styled(lockIcon)<{ $size: ContentChipSize }>`
	height: ${(p: { $size: ContentChipSize }) => DIMENSIONS[p.$size].lockGlyph}px;
	width: ${(p: { $size: ContentChipSize }) => DIMENSIONS[p.$size].lockGlyph}px;

	& path {
		fill: ${palette('white')};
	}
`;

export const PlayBadge = styled.div<{ $margin: number; $size: ContentChipSize }>`
	${chipBackground}
	${chipPosition}
	align-items: center;
	border-radius: 50%;
	box-sizing: border-box;
	display: flex;
	height: ${p => DIMENSIONS[p.$size].play}px;
	justify-content: center;
	width: ${p => DIMENSIONS[p.$size].play}px;
`;

export const DurationBadge = styled.div<{ $margin: number; $size: ContentChipSize }>`
	${chipBackground}
	${chipPosition}
	align-items: center;
	border-radius: 999px;
	box-sizing: border-box;
	color: ${palette('white')};
	display: flex;
	font-size: ${p => DIMENSIONS[p.$size].fontSize}px;
	font-weight: 500;
	gap: 4px;
	line-height: 1.2;
	padding: 4px 8px;
	white-space: nowrap;
`;

const glyphSize = css<{ $size: ContentChipSize }>`
	flex-shrink: 0;
	height: ${p => DIMENSIONS[p.$size].glyph}px;
	width: ${p => DIMENSIONS[p.$size].glyph}px;
`;

export const PlayGlyph = styled(playIcon)<{ $size: ContentChipSize }>`
	${glyphSize}
`;

export const VideoGlyph = styled(videoIcon)<{ $size: ContentChipSize }>`
	${glyphSize}
`;
